import { useState } from 'react';
import { Check, CircleCheck, CircleDashed } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { RoleDefinition } from '../../types';
import {
  useGrantEmployeeRole,
  useRevokeEmployeeRole,
} from '../../api/employees';

interface Props {
  employeeId: string;
  assignedRoles: string[];
  roleDefinitions: RoleDefinition[];
}

export function RoleAssignPanel({ employeeId, assignedRoles, roleDefinitions }: Props) {
  const [focusRole, setFocusRole] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const assigned = new Set(assignedRoles);

  const grant = useGrantEmployeeRole();
  const revoke = useRevokeEmployeeRole();

  const focusDef = focusRole
    ? roleDefinitions.find((r) => r.role === focusRole) ?? null
    : null;

  const toggle = async (role: string) => {
    if (assigned.has(role)) {
      await revoke.mutateAsync({ id: employeeId, role, reason: reason.trim() || undefined });
    } else {
      await grant.mutateAsync({ id: employeeId, role, reason: reason.trim() || undefined });
    }
    setReason('');
  };

  const busy = grant.isPending || revoke.isPending;

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-4">
      {/* Role list */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase text-muted-foreground">Roles</h4>
        <div className="space-y-1">
          {roleDefinitions.map((def) => {
            const isAssigned = assigned.has(def.role);
            const isFocused = focusRole === def.role;
            return (
              <div
                key={def.role}
                className={cn(
                  'w-full flex items-stretch justify-between gap-2 rounded border p-1 text-sm transition',
                  isFocused ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
                )}
              >
                <button
                  type="button"
                  onClick={() => setFocusRole(def.role)}
                  className="flex min-w-0 flex-1 items-center gap-2 rounded px-2 py-1 text-left"
                >
                  {isAssigned ? (
                    <CircleCheck className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <CircleDashed className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div>
                    <div className="font-medium">{def.label}</div>
                    <div className="text-[10px] text-muted-foreground">{def.role}</div>
                  </div>
                </button>
                <Button
                  size="sm"
                  className="self-center"
                  variant={isAssigned ? 'outline' : 'default'}
                  disabled={busy}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(def.role);
                  }}
                >
                  {isAssigned ? 'Revoke' : 'Grant'}
                </Button>
              </div>
            );
          })}
        </div>
        <div>
          <label className="text-xs text-muted-foreground" htmlFor="role-reason">
            Reason (optional — appears in audit log)
          </label>
          <Textarea
            id="role-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. promoted to Sales Manager"
          />
        </div>
      </div>

      {/* Role documentation panel */}
      <div className="rounded border bg-muted/30 p-3 text-sm">
        {!focusDef ? (
          <div className="text-xs text-muted-foreground italic">
            Select a role to see what it unlocks.
          </div>
        ) : (
          <div className="space-y-2">
            <div>
              <div className="font-semibold">{focusDef.label}</div>
              <div className="text-[10px] text-muted-foreground">{focusDef.role}</div>
            </div>
            {focusDef.description && (
              <p className="text-xs whitespace-pre-wrap">{focusDef.description}</p>
            )}
            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase text-muted-foreground">
                Capabilities
              </div>
              {focusDef.capabilities.length === 0 && (
                <div className="text-xs text-muted-foreground italic">No capabilities listed.</div>
              )}
              {focusDef.capabilities.map((cap, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs">
                  <Check
                    className={cn(
                      'h-3.5 w-3.5 mt-0.5 flex-shrink-0',
                      cap.access === 'read-write' ? 'text-emerald-600' : 'text-blue-600',
                    )}
                  />
                  <div>
                    <span className="font-medium">{cap.label}</span>
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      ({cap.access})
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
